import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Globe, Download, ArrowRight, ArrowLeft } from "lucide-react";
import api from "../lib/api";
import { useLang } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { QuotationForm } from "../components/QuotationForm";

export default function BrandDetail() {
  const { slug } = useParams();
  const { t, lang, isRTL } = useLang();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;
  const [brand, setBrand] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    api.get(`/brands/${slug}`).then((r) => setBrand(r.data)).catch(() => setBrand(null));
  }, [slug]);

  if (!brand) return <div className="py-32 text-center text-slate-500">Loading...</div>;

  const desc = lang === "ar" ? brand.description_ar : brand.description_en;

  return (
    <div data-testid="brand-detail-page">
      <section className="bg-[#0891b2] text-white py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Link to="/brands" className="text-white/80 hover:text-white text-sm inline-flex items-center gap-2 mb-4" data-testid="brand-back">
            <Arrow className="w-4 h-4 rotate-180" /> {t.brands.backToAll}
          </Link>
          <div className="flex items-center gap-3 mb-3">
            {brand.country && (
              <span className="flex items-center gap-1.5 text-red-200 text-sm">
                <Globe className="w-4 h-4" /> {brand.country}
              </span>
            )}
          </div>
          <h1 className="font-display text-5xl lg:text-6xl" data-testid="brand-name">{brand.name}</h1>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-8">
            <p className="text-slate-700 text-lg leading-relaxed">{desc}</p>

            {brand.categories?.length > 0 && (
              <div>
                <h2 className="font-display text-xl text-[#0891b2] mb-3">{t.brands.productCategories}</h2>
                <div className="flex flex-wrap gap-2">
                  {brand.categories.map((c, i) => (
                    <span key={i} className="px-3 py-1.5 bg-cyan-50 text-[#0891b2] rounded text-sm font-medium">{c}</span>
                  ))}
                </div>
              </div>
            )}

            {brand.featured_products?.length > 0 && (
              <div>
                <h2 className="font-display text-xl text-[#0891b2] mb-3">{t.brands.featuredProducts}</h2>
                <ul className="grid sm:grid-cols-2 gap-2">
                  {brand.featured_products.map((p, i) => (
                    <li key={i} className="bg-slate-50 border border-slate-200 rounded p-3 text-slate-700">{p}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <aside className="lg:sticky lg:top-24 h-fit space-y-3">
            {brand.catalog_url && (
              <a href={brand.catalog_url} target="_blank" rel="noreferrer">
                <Button className="w-full bg-white border-2 border-[#0891b2] text-[#0891b2] hover:bg-[#0891b2] hover:text-white h-12" data-testid="catalog-download">
                  <Download className="w-4 h-4 me-2" /> {t.brands.downloadCatalog}
                </Button>
              </a>
            )}
            <Button
              onClick={() => setShowForm(!showForm)}
              data-testid="brand-request-quote"
              className="w-full bg-[#ef4444] hover:bg-[#dc2626] text-white h-12 font-semibold"
            >
              {t.brands.requestQuote}
            </Button>
          </aside>
        </div>

        {showForm && (
          <div className="max-w-3xl mx-auto px-6 lg:px-10 mt-10 pt-10 border-t border-slate-200">
            <h2 className="font-display text-2xl text-[#0891b2] mb-6">{t.quotation.title}</h2>
            <QuotationForm projectReference={`${brand.name} equipment`} />
          </div>
        )}
      </section>
    </div>
  );
}
